import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Sparkles, Star, MapPin, CheckCircle2, MessageSquare, Clock, Users, BadgeCheck } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function MatchesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [annonce, setAnnonce] = useState(null);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [id, isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [annonceRes, matchesRes] = await Promise.all([
        api.get(`/annonces/${id}`),
        api.get(`/annonces/${id}/matches`)
      ]);
      setAnnonce(annonceRes.data);
      setMatches(matchesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des matchs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!annonce) {
    return (
      <Layout>
        <div className="container-app py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Annonce non trouvee</h1>
          <Link to="/dashboard">
            <Button>Retour au tableau de bord</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-4xl">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>

        {/* Annonce Summary */}
        <Card className="rounded-2xl mb-8 bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-2xl flex-shrink-0">
                {annonce.offer_category?.icon || '✨'}
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold mb-1">{annonce.title}</h1>
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge className="bg-primary">
                    Je propose: {annonce.offer_category?.name}
                  </Badge>
                  {annonce.open_to_all ? (
                    <Badge variant="secondary">Ouvert à tout</Badge>
                  ) : annonce.want_categories_details?.length > 0 && (
                    <Badge variant="secondary">
                      Je cherche: {annonce.want_categories_details.map(c => c.name).join(', ')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {annonce.location}
                  <span className="mx-2">•</span>
                  <Clock className="w-4 h-4" />
                  {annonce.duration_hours}h
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Matches Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Vos matchs</h2>
              <p className="text-sm text-muted-foreground">
                {matches.length} profil(s) compatible(s)
              </p>
            </div>
          </div>
        </div>

        {/* Matches List */}
        {matches.length > 0 ? (
          <div className="space-y-4">
            {matches.map((match, index) => (
              <Card
                key={match.id}
                className={`rounded-xl transition-all hover:shadow-md ${
                  match.is_perfect_match ? 'border-2 border-secondary ring-2 ring-secondary/20' : ''
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      index < 3 ? 'bg-secondary text-secondary-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={match.user?.avatar_url} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {match.user?.full_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/users/${match.user?.id}`}
                                className="font-semibold hover:text-primary transition-colors"
                              >
                                {match.user?.full_name || 'Utilisateur'}
                              </Link>
                              {match.user?.email_verified && (
                                <BadgeCheck className="w-4 h-4 text-primary" />
                              )}
                              {match.is_perfect_match && (
                                <Badge className="bg-secondary text-xs">
                                  <Star className="w-3 h-3 mr-1" />
                                  Match parfait
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {match.annonce?.location}
                            </p>
                          </div>
                        </div>

                        {/* Score */}
                        <div className="text-right flex-shrink-0">
                          <div className="text-2xl font-bold text-primary">{match.score}</div>
                          <div className="text-xs text-muted-foreground">points</div>
                        </div>
                      </div>

                      {/* Annonce Details */}
                      <div className="mt-3 p-3 rounded-lg bg-muted/50">
                        <p className="font-medium text-sm mb-2">{match.annonce?.title}</p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs">
                            {match.annonce?.offer_category?.icon} Propose: {match.annonce?.offer_category?.name}
                          </Badge>
                          {match.annonce?.want_categories_details?.slice(0, 2).map(cat => (
                            <Badge key={cat.id} variant="outline" className="text-xs">
                              Cherche: {cat.name}
                            </Badge>
                          ))}
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {match.annonce?.duration_hours}h
                          </Badge>
                        </div>
                      </div>

                      {/* Match Reasons */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {match.match_reasons?.map((reason, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            {reason}
                          </span>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="mt-4 flex gap-2">
                        <Link to={`/annonces/${match.id}`} className="flex-1">
                          <Button variant="outline" className="w-full rounded-full" size="sm">
                            Voir l&apos;annonce
                          </Button>
                        </Link>
                        <Link to={`/annonces/${match.id}/propose?from=${annonce.id}`} className="flex-1">
                          <Button className="w-full btn-primary rounded-full" size="sm" data-testid={`propose-exchange-${index}`}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Proposer un échange
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-xl">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Aucun match pour le moment</h3>
              <p className="text-muted-foreground mb-4">
                Revenez plus tard ou explorez les annonces disponibles
              </p>
              <Link to="/annonces">
                <Button variant="outline" className="rounded-full">
                  Explorer les annonces
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
